import { Button } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-field';
import { Label } from '@/components/ui/label';
import { Modal } from '@/components/ui/modal';
import { Typography } from '@/components/ui/typography';
import { Loader2, MapPin } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

const NOTES_MAX_LENGTH = 350;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface EditCancelRequestModalProps {
  headerText: string;
  subHeaderText: string;
  orderId: string;
  formattedAddress: string;
  placeHolderText: string;
  changeNotes: string | undefined;
  isValid: boolean;
  isLoader: boolean;
  tableAction: string;
  onSubmitEdit: () => void;
  onSubmitCancel: () => void;
  onCancel: () => void;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  inputRef: React.RefObject<HTMLTextAreaElement>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EditCancelRequestModal({
  headerText,
  subHeaderText,
  orderId,
  formattedAddress,
  placeHolderText,
  changeNotes,
  isValid,
  isLoader,
  tableAction,
  onSubmitEdit,
  onSubmitCancel,
  onCancel,
  onChange,
  inputRef
}: EditCancelRequestModalProps) {
  const onSubmit =
    tableAction === 'Edit Order' ? onSubmitEdit : onSubmitCancel;

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(open) => !open && onCancel()}
      title={
        <Typography variant="heading-lg" className="font-medium">
          {headerText}
        </Typography>
      }
      description={subHeaderText}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isLoader}
            onClick={onSubmit}
            className="w-full sm:w-auto"
          >
            {isLoader && <Loader2 className="h-4 w-4 animate-spin" />}
            Submit Request
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-start gap-3 rounded-md border border-border bg-muted p-4">
          <MapPin
            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <div className="flex flex-col gap-1">
            <Typography variant="body-medium">{formattedAddress}</Typography>
            {orderId && (
              <Typography variant="small">Order ID:{orderId}</Typography>
            )}
          </div>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <Label htmlFor="changeNotes" required className="mb-0">
              Notes
            </Label>
            <span className="text-xs text-muted-foreground">
              {changeNotes?.length || 0}/{NOTES_MAX_LENGTH}
            </span>
          </div>
          <Textarea
            id="changeNotes"
            name="changeNotes"
            rows={5}
            maxLength={NOTES_MAX_LENGTH}
            placeholder={placeHolderText}
            value={changeNotes}
            onChange={onChange}
            ref={inputRef}
            className={isValid ? 'border-destructive' : undefined}
            data-testid="changeNotes_id"
          />
          <FormError message={isValid ? 'Please enter notes' : undefined} />
        </div>
      </div>
    </Modal>
  );
}
